import { Component, inject, OnInit, ChangeDetectionStrategy } from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { Router } from '@angular/router';
import { AuthService } from '../../services/auth.service';
import { MatInput } from '@angular/material/input';
import { MatFormField, MatLabel, MatError } from '@angular/material/form-field';
import { MatCard } from '@angular/material/card';
import { environment } from 'src/environments/environment';
import { UserStateService } from 'src/app/services/signals/user-state.service';
import { User } from 'src/app/models/user';
import { ExpirationService } from 'src/app/services/expiration-service';
import { SettingsService } from 'src/app/services/settings.service';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';
import { ExternalParameters } from 'src/app/models/external-parameters';
import { MatIconModule } from '@angular/material/icon';
import {
  FaIconComponent,
  FontAwesomeModule,
} from '@fortawesome/angular-fontawesome';
import { ToastrService } from 'ngx-toastr';
import { faKey } from '@fortawesome/free-solid-svg-icons';
import { MatButtonModule } from '@angular/material/button';
import { CookieConsentService } from 'src/app/services/cookie-consent.service';
import { MatDialog } from '@angular/material/dialog';
import { CookiePopupComponent } from '../cookie-popup/cookie-popup.component';
import { UsersService } from 'src/app/services/users.service';
import { OrganizationsService } from 'src/app/services/organization.service';

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],

  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    MatCard,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    MatError,
    NgxSpinnerModule,
    MatIconModule,
    FontAwesomeModule,
    FaIconComponent,
    MatButtonModule,
    NgxSpinnerModule,
  ],
})
export class LoginComponent implements OnInit {
  version: string = environment.version;
  loginForm!: UntypedFormGroup;
  authenticationInvalid = false;
  expired = false;
  hide = true;

  public fakey = faKey;

  private spinner = inject(NgxSpinnerService);

  constructor(
    private router: Router,
    private formBuilder: UntypedFormBuilder,
    private authService: AuthService,
    private userStateService: UserStateService,
    private settingsService: SettingsService,
    private dialog: MatDialog,
    private cookieConsentService: CookieConsentService,
    private toastr: ToastrService,
    private usersService: UsersService,
    private organizationsService: OrganizationsService,
    private expirationService: ExpirationService, // Injecter le service d'expiration
  ) {}

  ngOnInit() {
    this.spinner.hide('spinner');

    this.loginForm = this.formBuilder.group({
      account: ['', Validators.required],
      password: ['', Validators.required],
    });

    this.expired = this.expirationService.isSessionExpired();

    // Vérifie si le consentement est manquant dès le chargement
    if (!this.cookieConsentService.hasConsent()) {
      setTimeout(() => {
        this.dialog.open(CookiePopupComponent, {
          disableClose: true,
          width: '700px',
        });
      }, 0); // évite une erreur ExpressionChangedAfterItHasBeenCheckedError
    }
  }

  onSubmit(): void {
    console.log('[LOGIN] Début login');

    if (!this.cookieConsentService.hasConsent()) {
      this.dialog.open(CookiePopupComponent, {
        disableClose: true,
        width: '700px',
      });
      return;
    }
    const { account, password } = this.loginForm.value;

    console.log('[LOGIN] Show spinner');
    this.spinner.show('spinner');

    console.log('[LOGIN] Appel AuthService.logon');
    this.authService.logon(account, password).subscribe({
      next: (isAuthenticated) => {
        console.log('[LOGIN] logon.next', isAuthenticated);

        if (!isAuthenticated) {
          console.log('[LOGIN] Authentification refusée');
          this.authenticationInvalid = true;
          this.spinner.hide('spinner');
          console.log('[LOGIN] Spinner caché');
          return;
        }

        console.log('[LOGIN] Chargement utilisateur');

        this.usersService.getCurrentUser().subscribe({
          next: (user) => {
            console.log('[LOGIN] Utilisateur reçu', user);

            const access =
              user?.properties?.find(
                (p: any) => p.name?.trim() === 'NexusCentrex',
              )?.value || '';

            console.log('[LOGIN] Droit NexusCentrex =', access);

            if (access.includes('0')) {
              console.log('[LOGIN] NO ACCESS');

              console.log('[LOGIN] Hide spinner');
              this.spinner.hide('spinner');
              console.log('[LOGIN] Spinner caché');

              this.toastr.error(
                "Vous n'avez pas les droits nécessaires pour accéder à Nexus Centrex.",
                'Accès refusé',
              );

              console.log('[LOGIN] removeCurrentUser');
              this.usersService.removeCurrentUser();

              console.log('[LOGIN] Fin NO ACCESS');
              return;
            }

            console.log('[LOGIN] Accès OK');

            this.userStateService.setCurrentUser(user);
            sessionStorage.setItem('user', JSON.stringify(user));

            console.log('[LOGIN] Appel getOrganizationId');

            this.usersService.getOrganizationId().subscribe({
              next: (orgIdObj: any) => {
                console.log('[LOGIN] organizationId reçu', orgIdObj);

                const orgId = orgIdObj?.organizationId;

                if (!orgId) {
                  console.warn('[LOGIN] organizationId null');
                  this.finishLogin();
                  return;
                }

                console.log('[LOGIN] Appel getOrganizationById', orgId);

                this.usersService.getOrganizationById(orgId).subscribe({
                  next: (org) => {
                    console.log('[LOGIN] Organisation reçue', org);

                    this.organizationsService.setCurrentOrganization(org);

                    console.log('[LOGIN] finishLogin');
                    this.finishLogin();
                  },
                  error: (err) => {
                    console.error('[LOGIN] Erreur getOrganizationById', err);
                    this.finishLogin();
                  },
                  complete: () => {
                    console.log('[LOGIN] getOrganizationById COMPLETE');
                  },
                });
              },
              error: (err) => {
                console.error('[LOGIN] Erreur getOrganizationId', err);
                this.finishLogin();
              },
              complete: () => {
                console.log('[LOGIN] getOrganizationId COMPLETE');
              },
            });
          },
          error: (err) => {
            console.error('[LOGIN] Erreur getCurrentUser', err);

            this.toastr.error(
              'Impossible de charger les informations utilisateur',
              'Erreur',
            );

            this.spinner.hide('spinner');
          },
          complete: () => {
            console.log('[LOGIN] getCurrentUser COMPLETE');
          },
        });
      },
      error: (err) => {
        console.error('[LOGIN] Erreur logon', err);

        this.authenticationInvalid = true;
        this.spinner.hide('spinner');
      },
      complete: () => {
        console.log('[LOGIN] logon COMPLETE');
      },
    });
  }

  private finishLogin() {
    console.log('[LOGIN] finishLogin()');
    this.expirationService.setSessionExpired(false);

    console.log('[LOGIN] Navigation vers /dashboard');
    this.router.navigate(['/dashboard']);
  }

  forgottenPassword(): void {
    this.spinner.show('spinner');

    this.settingsService.getSettings().subscribe({
      next: (x) => {
        let gumUrl: string | null;

        try {
          gumUrl = x.apps['gum']['url'];
        } catch {
          gumUrl = null;
        }

        if (gumUrl) {
          const parameters = new ExternalParameters();
          parameters.name = 'Nexus';
          parameters.return = `${window.location.origin}/login`;

          const redirectUrl = `${gumUrl}/forgottenpassword?parameters=${encodeURIComponent(
            JSON.stringify(parameters),
          )}`;

          window.location.href = redirectUrl;
        } else {
          this.toastr.warning(
            'Votre domaine n’est pas correctement configuré.',
            'Configuration manquante',
          );
        }
      },
      error: () => {
        this.toastr.error(
          'Erreur lors de la récupération des paramètres.',
          'Erreur',
        );
      },
      complete: () => {
        this.spinner.hide('spinner');
      },
    });
  }
}
